import type { LucideIcon } from 'lucide-react';

/**
 * What the `@` popup lists. An ENTRY, not a file: the popup, the trigger, the keyboard and the token
 * excision in `useComposer` know nothing beyond this shape, so a new source is a new builder of entries
 * and nothing else.
 *
 * That includes what picking one DOES. Two of today's kinds become an attachment chip, but the action
 * belongs to the entry rather than to the popup — a kind whose pick means something else entirely fits
 * without the popup learning about it.
 */

/** Which source an entry came from. Also the section it is listed under — see {@link mentionSections}. */
export type MentionEntryKind = 'artifact' | 'sharepoint';

/** One row of the popup. */
export interface MentionEntry {
  /**
   * What identifies this row among the rows on screen. The highlight is tracked by this key rather than
   * by a position, so rows arriving from a slower source cannot move the row the user is about to pick.
   */
  key: string;
  kind: MentionEntryKind;
  icon: LucideIcon;
  /** The first line — what the user typed part of. */
  label: string;
  /** The second line: what this is and where it is from. Empty when the source has nothing to add. */
  detail: string;
  /**
   * What a screen reader announces for the row. It names the SOURCE, because the two lists are one list
   * here and a repeated file name is ordinary — {@link detail} alone tells the sources apart by eye only.
   */
  accessibleName: string;
  /** What picking this row does. Bound by the source that built the entry. */
  select: () => void;
}

/** The entries of one source, under the heading it is listed by. */
export interface MentionSection {
  kind: MentionEntryKind;
  heading: string;
  entries: MentionEntry[];
}

/**
 * How many rows one source may contribute. The popup is driven from the keyboard, so an unbounded list
 * turns ArrowDown into a scrolling marathon; the answer to "mine is not in the list" is to type more of
 * the name, which every source narrows on.
 */
export const MENTION_ROWS_PER_SOURCE = 5;

/**
 * The sources, in the order their sections appear. This list is the only place that order lives: a source
 * that answers late still lands in its own section at its own position, so nothing already on screen moves
 * under the user's Enter.
 */
const SECTION_ORDER: readonly { kind: MentionEntryKind; heading: string }[] = [
  { kind: 'artifact', heading: 'Artifacts' },
  { kind: 'sharepoint', heading: 'SharePoint' },
];

/**
 * Groups the entries into the sections the popup renders, keeping each source's own order and dropping a
 * source that found nothing — a heading with no rows under it promises rows that are not coming.
 */
export function mentionSections(entries: MentionEntry[]): MentionSection[] {
  return SECTION_ORDER.map(({ kind, heading }) => ({
    kind,
    heading,
    entries: entries.filter((entry) => entry.kind === kind),
  })).filter((section) => section.entries.length > 0);
}
